import type { TokenResponse } from '@/stores/auth/types'

/**
 * Encodes a given ArrayBuffer to base64 URL-safe format.
 *
 * @param str - The ArrayBuffer to be encoded.
 * @returns The base64 URL-safe encoded string.
 */
export const base64URLEncode = (str: ArrayBuffer) => {
  return btoa(String.fromCharCode.apply(null, Array.from(new Uint8Array(str))))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '')
}

/**
 * Generates a secure nonce using cryptographic functions.
 *
 * @returns {Promise<string>} - A promise that resolves with the generated nonce.
 */
export const generateNonce = async () => {
  const hash = await sha256(crypto.getRandomValues(new Uint32Array(4)))
  // https://developer.mozilla.org/en-US/docs/Web/API/SubtleCrypto/digest
  const hashArray = Array.from(new Uint8Array(hash))
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('')
}

/**
 * Calculates the SHA-256 hash of an ArrayBuffer.
 *
 * @param arrayBuffer - The input ArrayBuffer to calculate the hash from.
 * @returns - A Promise that resolves with the calculated SHA-256 hash as an ArrayBuffer.
 */
export const sha256 = async (arrayBuffer: ArrayBuffer) => {
  return await crypto.subtle.digest('SHA-256', arrayBuffer)
}

/**
 * Converts a string to an ArrayBuffer.
 *
 * @param {string} str The string to be*/
export const str2ab = (str: string): ArrayBuffer => {
  const buf = new ArrayBuffer(str.length) // 2 bytes for each char
  const bufView = new Uint8Array(buf)
  for (let i = 0, strLen = str.length; i < strLen; i++) {
    bufView[i] = str.charCodeAt(i)
  }
  return buf
}

/**
 * A non-2xx answer from the token endpoint. A request that never reached the
 * provider rejects with fetch's own `TypeError` instead, so `status` is what
 * tells a refusal from a network error.
 */
export class TokenEndpointError extends Error {
  constructor(readonly status: number) {
    super(`Request failed with status code ${status}`)
    this.name = 'TokenEndpointError'
  }
}

/**
 * POSTs a grant to the token endpoint as a form, the encoding OAuth requires.
 * Undefined fields are left out and nulls sent empty, as axios did before this.
 *
 * @param url - The provider's token endpoint.
 * @param grant - The grant's form fields.
 * @returns The parsed token response.
 */
export const postTokenRequest = async (
  url: string,
  grant: Record<string, string | null | undefined>,
): Promise<TokenResponse> => {
  const body = new URLSearchParams()
  for (const [key, value] of Object.entries(grant)) {
    if (value !== undefined) {
      body.append(key, value ?? '')
    }
  }
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  })
  if (!res.ok) {
    throw new TokenEndpointError(res.status)
  }
  return res.json()
}
